import { client } from '@/lib/api/client'
import {
  venueCampaignPerformanceExportPath,
  venueCampaignPerformancePath,
  venueCampaignPerformanceSummaryPath,
  venueNoticeReadStatusPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'
import { downloadBlob } from '@/lib/utils/downloadFile'

import type {
  VenueCampaignPerformanceListView,
  VenueCampaignPerformanceSummaryView,
  VenueNoticeReadStatusView,
} from './campaignPerformanceTypes'
import {
  performanceExportFileName,
  performanceQueryParams,
  type PerformanceListFilter,
} from './campaignPerformanceViewModel'

/** `GET campaigns/performance/summary`(#734 §1-2). 게이팅 없음 — 이용권 만료와 무관하게 항상 200. */
export async function fetchCampaignPerformanceSummary(
  venueId: string,
  params: { from: string; to: string }
): Promise<VenueCampaignPerformanceSummaryView> {
  try {
    const { data } = await client.get<VenueCampaignPerformanceSummaryView>(
      venueCampaignPerformanceSummaryPath(venueId),
      { params }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET campaigns/performance`(#734 §1-3) — 커서 페이지네이션. `SENT`+`PROMOTION`만 담는다.
 * 🚨 `fetchCampaigns`(작성일 축)의 커서와 호환되지 않는다 — 절대 서로 되싣지 않는다.
 */
export async function fetchCampaignPerformance(
  venueId: string,
  filter: PerformanceListFilter,
  cursor?: string
): Promise<VenueCampaignPerformanceListView> {
  try {
    const { data } = await client.get<VenueCampaignPerformanceListView>(venueCampaignPerformancePath(venueId), {
      params: performanceQueryParams(filter, cursor),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET campaigns/performance/export`(#734 §1-4) — 성과 CSV 다운로드.
 * 🚨 목록과 **같은 조건 해석기**(`performanceQueryParams`)를 쓴다 — 화면에 보이는 것과 파일 내용이 일치한다.
 */
export async function downloadCampaignPerformanceCsv(venueId: string, filter: PerformanceListFilter): Promise<void> {
  try {
    const { data } = await client.get<Blob>(venueCampaignPerformanceExportPath(venueId), {
      params: performanceQueryParams(filter),
      responseType: 'blob',
    })
    downloadBlob(data, performanceExportFileName(venueId, filter.from, filter.to))
  } catch (error) {
    throw toApiError(error)
  }
}

/** `GET campaigns/notice-read-status`(#734 §1-5) — 운영 공지 전용 열람 현황(비용·전환 필드 없음). */
export async function fetchNoticeReadStatus(
  venueId: string,
  params: { from: string; to: string }
): Promise<VenueNoticeReadStatusView> {
  try {
    const { data } = await client.get<VenueNoticeReadStatusView>(venueNoticeReadStatusPath(venueId), { params })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}
